import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useDateRange } from "@/context/DateRangeContext";
import { inr, pct, startOfMonth, todayISO } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KpiCard } from "@/components/KpiCard";
import AuditPanel from "@/components/AuditPanel";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, ReferenceLine, Legend,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { FileDown, ShieldAlert } from "lucide-react";

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="border border-border p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">{subtitle}</div>
          <h3 className="font-display text-lg font-semibold mt-1">{title}</h3>
        </div>
      </div>
      <div className="h-[280px]">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 12,
};

const shortDate = (d) => {
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

export default function Dashboard() {
  const { current } = useWorkspace();
  const { dateFrom, dateTo, setDateFrom, setDateTo, setRange } = useDateRange();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/dashboard/summary", { params: { workspace_id: current.id, date_from: dateFrom, date_to: dateTo } }),
        api.get("/dashboard/audit", { params: { workspace_id: current.id, date_from: dateFrom, date_to: dateTo } }),
      ]);
      setData(s.data);
      setAudit(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [current, dateFrom, dateTo]);

  const chartData = useMemo(() => (data?.daily || []).map((d) => ({ ...d, label: shortDate(d.date) })), [data]);
  const kpis = data?.kpis;

  const quickSet = (days) => {
    const to = new Date();
    const from = new Date(); from.setDate(to.getDate() - days + 1);
    setRange(from.toISOString().slice(0, 10), to.toISOString().slice(0, 10));
  };

  const openReport = () => {
    if (!current) return;
    const url = `/report/weekly?workspace_id=${current.id}&date_from=${dateFrom}&date_to=${dateTo}&brand=${encodeURIComponent(current.name)}`;
    window.open(url, "_blank");
  };

  const auditAlertCount = audit
    ? Number(!!audit.flags.bleeding_skus.triggered) + Number(!!audit.flags.discount_risk.triggered) + Number(!!audit.flags.roas_efficiency.triggered)
    : 0;

  if (!current) {
    return (
      <div className="p-12">
        <div className="overline text-muted-foreground">No workspace</div>
        <h1 className="font-display text-3xl font-bold mt-2">Create or select a workspace to begin.</h1>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-border p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-muted-foreground">{current.brand_name}</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-title">
              Executive P&L
            </h1>
          </div>
          <div className="flex items-end gap-3 flex-wrap" data-testid="date-filter">
            <div>
              <Label className="overline">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="date-from" />
            </div>
            <div>
              <Label className="overline">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="date-to" />
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => quickSet(7)} className="rounded-none" data-testid="range-7d">7d</Button>
              <Button variant="outline" size="sm" onClick={() => quickSet(30)} className="rounded-none" data-testid="range-30d">30d</Button>
              <Button variant="outline" size="sm" onClick={() => setRange(startOfMonth(), todayISO())} className="rounded-none" data-testid="range-mtd">MTD</Button>
            </div>
            <Button onClick={openReport} className="rounded-none h-10" data-testid="export-pdf-btn">
              <FileDown className="h-4 w-4 mr-2" /> Export Executive Memo
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <div className="border-b border-border px-8">
          <TabsList className="bg-transparent rounded-none h-auto p-0 gap-6" data-testid="dash-tabs">
            <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm" data-testid="tab-overview">
              Executive P&amp;L
            </TabsTrigger>
            <TabsTrigger value="returns" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm" data-testid="tab-returns">
              Returns &amp; RTO
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:font-semibold px-0 py-3 h-auto text-sm flex items-center gap-2" data-testid="tab-audit">
              Audit &amp; Health Check
              {auditAlertCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[hsl(var(--negative))] text-[hsl(var(--negative))] text-[10px] font-mono">
                  <ShieldAlert className="h-3 w-3" />
                  {auditAlertCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-0">
          {/* KPI Grid */}
          <div className="border-b border-border">
            {loading || !kpis ? (
              <div className="grid grid-cols-2 md:grid-cols-4 grid-skeleton">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="p-6 min-h-[130px]"><Skeleton className="h-4 w-20 mb-4" /><Skeleton className="h-8 w-32" /></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 grid-skeleton" data-testid="kpi-grid">
                <KpiCard label="Gross Revenue" value={kpis.gross_revenue} sublabel={`${kpis.total_orders} orders · ${kpis.orders_source === "orders_collection" ? "from imported orders" : "from daily log"}`} format="inr" compact testid="kpi-gross" />
                <KpiCard label="Discounts Applied" value={kpis.discounts} sublabel={`${pct(kpis.discount_pct)} of gross`} format="inr" tone="negative" compact testid="kpi-discount" />
                <KpiCard label="Refunds Deducted" value={kpis.refunded_amount || 0} sublabel={`${kpis.returns_count || 0} returns`} format="inr" tone="negative" compact testid="kpi-refunds" />
                <KpiCard label="Net Revenue (Excl. GST)" value={kpis.net_revenue_ex_gst || 0} sublabel={`Avg GST ${pct(kpis.avg_gst_pct || 18, 0)} · tax-stripped`} format="inr" compact testid="kpi-net-revenue" />
                <KpiCard label="Total Ad Spend" value={kpis.total_ad_spend} sublabel={`Meta ${inr(kpis.meta_ad_spend, { compact: true })} · Google ${inr(kpis.google_ad_spend, { compact: true })}`} format="inr" compact testid="kpi-ad-spend" />
                <KpiCard label="Contribution Margin" value={kpis.contribution_margin} sublabel="Net Rev (Ex-GST) − COGS" format="inr" tone={kpis.contribution_margin >= 0 ? "positive" : "negative"} compact testid="kpi-cm" />
                <KpiCard label="Real Net Profit" value={kpis.real_net_profit} sublabel="Ex-GST, after all costs" format="inr" tone={kpis.real_net_profit >= 0 ? "positive" : "negative"} compact testid="kpi-net-profit" />
                <KpiCard label="Blended ROAS" value={kpis.blended_roas} sublabel="Net Rev (Ex-GST) / Ads" format="x" tone={kpis.blended_roas >= 3 ? "positive" : "neutral"} testid="kpi-roas" />
                <KpiCard label="Net Margin" value={kpis.net_margin_pct} sublabel="On Ex-GST Net Rev" format="pct" tone={kpis.net_margin_pct >= 10 ? "positive" : kpis.net_margin_pct < 0 ? "negative" : "neutral"} testid="kpi-margin" />
                <KpiCard
                  label="COD Mix"
                  value={kpis.cod_mix_pct || 0}
                  sublabel={`${kpis.cod_orders || 0} COD · ${kpis.prepaid_orders || 0} Prepaid${kpis.unknown_pm_orders ? ` · ${kpis.unknown_pm_orders} unknown` : ""}`}
                  format="pct"
                  tone={(kpis.cod_mix_pct || 0) > 60 ? "negative" : (kpis.cod_mix_pct || 0) < 30 ? "positive" : "neutral"}
                  testid="kpi-cod-mix"
                />
              </div>
            )}
          </div>

          {/* Returns & RTO Impact strip - moved to Returns tab */}
          {false && kpis && (kpis.returns_count > 0 || (kpis.refunded_amount || 0) > 0) && (
            <div className="border-b border-border" data-testid="returns-impact">
              <div className="px-8 py-3 flex items-center gap-3 border-b border-border">
                <div className="overline text-muted-foreground">Returns & RTO Impact</div>
                <div className="text-xs text-muted-foreground font-mono">{kpis.returns_count} returns in period</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 grid-skeleton">
                <KpiCard label="Return & RTO Rate" value={kpis.rto_rate_pct || 0} format="pct" tone={(kpis.rto_rate_pct || 0) > 12 ? "negative" : "neutral"} sublabel="Returns / Orders" testid="kpi-rto-rate" />
                <KpiCard label="Reverse Shipping" value={kpis.reverse_shipping || 0} format="inr" tone="negative" compact sublabel="Return logistics" testid="kpi-reverse-shipping" />
                <KpiCard label="RTO Fees" value={kpis.rto_fee || 0} format="inr" tone="negative" compact sublabel="Courier penalties" testid="kpi-rto-fee" />
                <KpiCard label="COGS Reclaimed" value={kpis.cogs_reclaimed || 0} format="inr" tone="positive" compact sublabel="Restocked inventory" testid="kpi-cogs-reclaimed" />
                <KpiCard label="COGS Lost" value={kpis.cogs_lost || 0} format="inr" tone="negative" compact sublabel="Damaged / broken" testid="kpi-cogs-lost" />
              </div>
            </div>
          )}

          <div className="p-8 space-y-6">
            {/* P&L Breakdown — line-item waterfall */}
            {kpis && (
              <div className="border border-border" data-testid="pnl-breakdown">
                <div className="px-6 py-4 border-b border-border flex items-baseline justify-between">
                  <div>
                    <div className="overline text-muted-foreground">Statement</div>
                    <h3 className="font-display text-lg font-semibold mt-1">P&amp;L Breakdown</h3>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{dateFrom} → {dateTo}</div>
                </div>
                <div className="divide-y divide-border">
                  {(() => {
                    const gst = Math.max(0, (kpis.net_revenue || 0) - (kpis.net_revenue_ex_gst || 0));
                    const rows = [
                      { label: "Gross Revenue", value: kpis.gross_revenue, kind: "revenue" },
                      { label: "Discounts", value: -(kpis.discounts || 0), kind: "expense" },
                      { label: "Refunds", value: -(kpis.refunded_amount || 0), kind: "expense" },
                      { label: "Net Revenue", value: kpis.net_revenue, kind: "subtotal" },
                      { label: "GST (stripped)", value: -gst, kind: "expense", sub: `${pct(kpis.avg_gst_pct || 18, 0)} avg` },
                      { label: "Net Revenue (Ex-GST)", value: kpis.net_revenue_ex_gst, kind: "subtotal" },
                      { label: "COGS", value: -(kpis.total_cogs || 0), kind: "expense" },
                      { label: "Primary Shipping", value: -(kpis.primary_shipping || 0), kind: "expense", sub: "Delivered orders" },
                      { label: "Contribution Margin", value: kpis.contribution_margin, kind: "subtotal" },
                      { label: "Ad Spend", value: -(kpis.total_ad_spend || 0), kind: "expense", sub: `Meta ${inr(kpis.meta_ad_spend || 0, { compact: true })} · Google ${inr(kpis.google_ad_spend || 0, { compact: true })}` },
                      { label: "Channel Commissions", value: -(kpis.total_marketplace_fees || 0), kind: "expense", sub: "Marketplace + platform fees" },
                      { label: "PG Fees", value: -(kpis.pg_cost || 0), kind: "expense", sub: "Incl. 18% GST" },
                      { label: "RTO Freight Loss", value: -(kpis.rto_freight_loss || 0), kind: "expense", sub: `Reverse ${inr(kpis.reverse_shipping || 0, { compact: true })} + Penalty ${inr(kpis.rto_fee || 0, { compact: true })} + Packaging ${inr(kpis.packaging_returned || 0, { compact: true })}` },
                      { label: "Real Net Profit", value: kpis.real_net_profit, kind: "total" },
                    ];
                    return rows.map((r, i) => {
                      const isTotal = r.kind === "total";
                      const isSub = r.kind === "subtotal";
                      const negative = (r.value || 0) < 0;
                      return (
                        <div
                          key={i}
                          className={`px-6 py-3 flex items-center justify-between ${isTotal ? "bg-accent/40" : isSub ? "bg-accent/20" : ""}`}
                          data-testid={`pnl-row-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <div>
                            <div className={`${isTotal ? "font-display font-semibold text-base" : isSub ? "font-medium text-sm" : "text-sm"}`}>
                              {r.label}
                            </div>
                            {r.sub && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{r.sub}</div>}
                          </div>
                          <div className={`font-mono ${isTotal ? "text-xl font-semibold" : "text-base"} ${
                            isTotal
                              ? (r.value || 0) >= 0 ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"
                              : negative
                                ? "text-[hsl(var(--negative))]"
                                : isSub ? "font-semibold" : ""
                          }`}>
                            {negative ? "(" : ""}{inr(Math.abs(r.value || 0), { decimals: 0 })}{negative ? ")" : ""}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Margin Matrix — Net Margin % split by payment method */}
            {kpis && kpis.margin_matrix && (
              <div className="border border-border" data-testid="margin-matrix">
                <div className="px-6 py-4 border-b border-border flex items-baseline justify-between">
                  <div>
                    <div className="overline text-muted-foreground">Split</div>
                    <h3 className="font-display text-lg font-semibold mt-1">Margin Matrix · Prepaid vs COD</h3>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">Ad spend allocated by Net Rev (Ex-GST) share</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                  {["prepaid", "cod"].map((pm) => {
                    const m = kpis.margin_matrix[pm] || {};
                    const positive = (m.net_margin_pct || 0) >= 0;
                    return (
                      <div key={pm} className="p-6" data-testid={`margin-matrix-${pm}`}>
                        <div className="flex items-baseline justify-between mb-4">
                          <div>
                            <div className="overline text-muted-foreground">{pm === "prepaid" ? "Prepaid" : "COD"}</div>
                            <div className="text-[10px] font-mono text-muted-foreground mt-1">
                              {m.orders || 0} orders · {m.units || 0} units
                            </div>
                          </div>
                          <div className={`font-mono text-3xl font-semibold ${positive ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"}`}>
                            {(m.net_margin_pct || 0).toFixed(2)}%
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-y-2 text-xs font-mono">
                          <div className="text-muted-foreground">Net Rev (Ex-GST)</div>
                          <div className="text-right">{inr(m.net_revenue_ex_gst || 0, { compact: true })}</div>
                          <div className="text-muted-foreground">COGS</div>
                          <div className="text-right text-[hsl(var(--negative))]">({inr(m.cogs || 0, { compact: true })})</div>
                          <div className="text-muted-foreground">Shipping</div>
                          <div className="text-right text-[hsl(var(--negative))]">({inr(m.shipping || 0, { compact: true })})</div>
                          <div className="text-muted-foreground">PG Fees</div>
                          <div className="text-right text-[hsl(var(--negative))]">({inr(m.pg_cost || 0, { compact: true })})</div>
                          <div className="text-muted-foreground">Channel Fees</div>
                          <div className="text-right text-[hsl(var(--negative))]">({inr(m.marketplace_fees || 0, { compact: true })})</div>
                          <div className="text-muted-foreground">Ad Spend (alloc)</div>
                          <div className="text-right text-[hsl(var(--negative))]">({inr(m.ad_spend_alloc || 0, { compact: true })})</div>
                          <div className="font-semibold text-foreground pt-2 border-t border-border/60 mt-1">Real Net Profit</div>
                          <div className={`text-right font-semibold pt-2 border-t border-border/60 mt-1 ${positive ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]"}`}>
                            {inr(m.real_net_profit || 0, { compact: true })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Net Revenue vs. Total Ad Spend" subtitle="Daily" testid="chart-rev-ad">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => inr(v, { compact: true })} width={60} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => inr(v)} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="net_revenue" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} name="Net Revenue" />
                    <Line type="monotone" dataKey="total_ad_spend" stroke="hsl(var(--negative))" strokeWidth={2} dot={false} name="Ad Spend" />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Discount Impact" subtitle="% of gross revenue lost" testid="chart-discount">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="disc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--negative))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--negative))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={40} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
                    <Area type="monotone" dataKey="discount_pct" stroke="hsl(var(--negative))" strokeWidth={2} fill="url(#disc)" name="Discount %" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard title="Net Profit Margin over time" subtitle="Real net / net revenue" testid="chart-margin">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={40} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="net_margin_pct" stroke="hsl(var(--positive))" strokeWidth={2} dot={false} name="Net Margin %" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </TabsContent>

        <TabsContent value="returns" className="mt-0 p-8" data-testid="returns-tab-content">
          {kpis && (kpis.returns_count > 0 || (kpis.refunded_amount || 0) > 0) ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-5 grid-skeleton border border-border" data-testid="returns-impact">
                <KpiCard label="Return & RTO Rate" value={kpis.rto_rate_pct || 0} format="pct" tone={(kpis.rto_rate_pct || 0) > 12 ? "negative" : "neutral"} sublabel="Returns / Orders" testid="kpi-rto-rate" />
                <KpiCard label="Refunds Deducted" value={kpis.refunded_amount || 0} format="inr" tone="negative" compact sublabel={`${kpis.returns_count} returns`} testid="kpi-refunds-detail" />
                <KpiCard label="Reverse Shipping" value={kpis.reverse_shipping || 0} format="inr" tone="negative" compact sublabel="Return logistics" testid="kpi-reverse-shipping" />
                <KpiCard label="RTO / Courier Penalties" value={kpis.rto_fee || 0} format="inr" tone="negative" compact sublabel="NDR fees" testid="kpi-rto-fee" />
                <KpiCard
                  label="RTO Leakage"
                  value={kpis.rto_leakage || 0}
                  format="inr"
                  tone="negative"
                  compact
                  sublabel={`Forward ${inr(kpis.rto_forward_shipping || 0, { compact: true })} + Reverse ${inr(kpis.reverse_shipping || 0, { compact: true })}`}
                  testid="kpi-rto-leakage"
                />
                <KpiCard label="COGS Reclaimed" value={kpis.cogs_reclaimed || 0} format="inr" tone="positive" compact sublabel="Restocked" testid="kpi-cogs-reclaimed" />
                <KpiCard label="COGS Lost" value={kpis.cogs_lost || 0} format="inr" tone="negative" compact sublabel="Damaged / broken" testid="kpi-cogs-lost" />
                <KpiCard label="Total Fulfillment Cost" value={(kpis.shipping_fee || 0) + (kpis.reverse_shipping || 0) + (kpis.rto_fee || 0)} format="inr" tone="negative" compact sublabel="Ship + Reverse + RTO" testid="kpi-fulfillment" />
              </div>
              <div className="border border-border p-6 text-sm text-muted-foreground">
                <div className="overline text-muted-foreground mb-2">Method</div>
                <div>Refunded amounts are deducted from tax-exclusive Net Revenue. COGS Reclaimed re-credits inventory value for restocked returns. COGS Lost captures damaged/broken units that were written off. All margin math on the Executive P&amp;L uses Net Revenue (Excl. GST).</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No returns imported for this range. Use <span className="font-mono">Sales & Ad Log → Upload Returns / RTO File</span> to import Shopify refund or NDR reports.</div>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-0 p-8">
          <AuditPanel audit={audit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
